import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

/**
 * OAuth Callback Page
 * 
 * Handles TWO separate auth flows:
 * 1. Microsoft Graph OAuth (type=microsoft) - sends token to parent dialog
 * 2. Supabase Auth (default) - sets Supabase session
 */
const AuthCallback = () => {
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing authentication...');

  useEffect(() => {
    // Check auth type from URL
    const urlParams = new URLSearchParams(window.location.search);
    const authType = urlParams.get('type');
    const isMicrosoftAuth = authType === 'microsoft';
    
    console.log('[AuthCallback] === OAUTH CALLBACK ===');
    console.log('[AuthCallback] Auth Type:', isMicrosoftAuth ? 'Microsoft Graph' : 'Supabase');
    console.log('[AuthCallback] Current URL:', window.location.href);
    console.log('[AuthCallback] Hash:', window.location.hash);

    const processCallback = () => {
      try {
        const hash = window.location.hash;
        
        // Extract access_token from URL fragment
        const tokenMatch = hash.match(/access_token=([^&]+)/);
        const errorMatch = hash.match(/error=([^&]+)/);
        const errorDescMatch = hash.match(/error_description=([^&]+)/);

        if (tokenMatch) {
          const token = tokenMatch[1];
          console.log('[AuthCallback] ✅ Token extracted successfully');
          console.log('[AuthCallback] Token length:', token.length);
          
          if (isMicrosoftAuth) {
            // Microsoft Graph Auth Flow: Send token to parent dialog
            setStatus('success');
            setMessage('Microsoft authentication successful! Closing...');

            if (Office?.context?.ui) {
              console.log('[AuthCallback] Sending Microsoft token to parent via messageParent');
              Office.context.ui.messageParent(JSON.stringify({
                status: 'success',
                token: token
              }));
            } else {
              console.error('[AuthCallback] ❌ Office.context.ui not available');
              setStatus('error');
              setMessage('Office.js not available. Please try again.');
            }
          } else {
            // Supabase Auth Flow: This would be for Supabase login
            // Currently not implemented, but placeholder for future use
            setStatus('success');
            setMessage('Authentication successful!');
            console.log('[AuthCallback] Supabase auth not yet implemented');
          }
        } else if (errorMatch) {
          const error = errorMatch[1];
          const errorDesc = errorDescMatch ? decodeURIComponent(errorDescMatch[1].replace(/\+/g, ' ')) : 'Unknown error';
          
          console.error('[AuthCallback] ❌ OAuth error:', error, errorDesc);
          setStatus('error');
          setMessage(`Authentication failed: ${errorDesc}`);

          // Send error back to parent if Microsoft auth
          if (isMicrosoftAuth && Office?.context?.ui) {
            Office.context.ui.messageParent(JSON.stringify({
              status: 'error',
              error: error,
              error_description: errorDesc
            }));
          }
        } else {
          console.error('[AuthCallback] ❌ No token or error found in URL');
          setStatus('error');
          setMessage('No authentication data received. Please try again.');
        }
      } catch (err) {
        console.error('[AuthCallback] ❌ Error processing callback:', err);
        setStatus('error');
        setMessage('An error occurred during authentication.');
        
        if (isMicrosoftAuth && Office?.context?.ui) {
          Office.context.ui.messageParent(JSON.stringify({
            status: 'error',
            error: 'processing_error',
            error_description: err instanceof Error ? err.message : 'Unknown error'
          }));
        }
      }
    };

    // Process callback immediately - Office.js is already loaded by index.html
    processCallback();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-3">
        {status === 'processing' && (
          <>
            <Loader2 className="h-10 w-10 animate-spin mx-auto text-primary" />
            <p className="text-sm text-muted-foreground">{message}</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="h-10 w-10 mx-auto rounded-full bg-green-500/10 flex items-center justify-center">
              <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-sm text-muted-foreground">{message}</p>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div className="h-10 w-10 mx-auto rounded-full bg-destructive/10 flex items-center justify-center">
              <svg className="h-5 w-5 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-sm text-destructive">{message}</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
